import * as React from "react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { buttonVariants } from "@/components/ui/button";
import { cn } from "@/lib/utils";

export interface ConfirmOptions {
  /** Título do diálogo (obrigatório). */
  title: string;
  /** Texto de apoio. ReactNode para permitir negrito/multilinha (ex.: aviso de cascade). */
  description?: React.ReactNode;
  /** Rótulo do botão de confirmação. Default: "Excluir". */
  confirmLabel?: string;
  /** Rótulo do botão de cancelar. Default: "Cancelar". */
  cancelLabel?: string;
  /** Estilo destrutivo no botão de confirmação. Default: true. */
  destructive?: boolean;
}

type ConfirmFn = (options: ConfirmOptions) => Promise<boolean>;

interface PendingState {
  options: ConfirmOptions;
  resolve: (value: boolean) => void;
}

const ConfirmContext = React.createContext<ConfirmFn | undefined>(undefined);

/**
 * Provider global de confirmação. Monta um único AlertDialog na raiz e expõe
 * `useConfirm()`, que retorna uma função `confirm(options) => Promise<boolean>`.
 *
 * Uso:
 *   const confirm = useConfirm();
 *   if (!(await confirm({ title: "Excluir X", description: "..." }))) return;
 *   mutation.mutate(id);
 *
 * O diálogo é renderizado fora de qualquer dropdown/menu (na raiz), evitando o
 * conflito de focus-trap do Radix quando o gatilho vive dentro de um DropdownMenu.
 */
export function ConfirmDialogProvider({ children }: { children: React.ReactNode }) {
  const [pending, setPending] = React.useState<PendingState | null>(null);
  // Ref autoritativa: garante liquidação única mesmo quando Action(onClick) e
  // onOpenChange(false) disparam no mesmo tick (sem re-render entre eles).
  const pendingRef = React.useRef<PendingState | null>(null);

  const confirm = React.useCallback<ConfirmFn>((options) => {
    return new Promise<boolean>((resolve) => {
      const state: PendingState = { options, resolve };
      pendingRef.current = state;
      setPending(state);
    });
  }, []);

  const settle = React.useCallback((value: boolean) => {
    const current = pendingRef.current;
    if (!current) return;
    pendingRef.current = null;
    current.resolve(value);
    setPending(null);
  }, []);

  const options = pending?.options;

  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      <AlertDialog
        open={!!pending}
        onOpenChange={(open) => {
          if (!open) settle(false);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{options?.title}</AlertDialogTitle>
            {options?.description != null && (
              <AlertDialogDescription>{options.description}</AlertDialogDescription>
            )}
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => settle(false)}>
              {options?.cancelLabel ?? "Cancelar"}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={() => settle(true)}
              className={cn(
                options?.destructive !== false &&
                  buttonVariants({ variant: "destructive" })
              )}
            >
              {options?.confirmLabel ?? "Excluir"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </ConfirmContext.Provider>
  );
}

/** Retorna a função de confirmação global. Lança se usada fora do provider. */
export function useConfirm(): ConfirmFn {
  const ctx = React.useContext(ConfirmContext);
  if (!ctx) {
    throw new Error("useConfirm deve ser usado dentro de um ConfirmDialogProvider");
  }
  return ctx;
}
